import {
  LabwareSourceInput,
  SlotFillingRuleInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';

import {
  type AllTaskProperties,
  type InstrumentForLocation,
  PropertiesType,
  type TaskProperties,
} from '../../../types';
import {
  isSlotAllocatingInstrument,
  isTaskDoesNotSupportSlotAllocating,
  supportsSlotRanges,
} from '../../../utils/instrumentCapabilities';
import { LabwareAndSourceTaskByTaskId } from '../../selectors/selectLabwareAndSourceNodeByTaskId';
import type { LabwareByHandleIndexByTaskId } from '../../selectors/types';
import {
  MaybeWorkflowSerializationErrors,
  WorkflowSerializationError,
} from '../../shared/types';
import {
  parseSlotString,
  parsedSlotToSlotRangeInput,
} from '../../shared/utils';
import * as TaskErrors from '../taskErrors';
import { err, ok } from '../utils';

export type GetActionTaskLabwareSourcesProps = {
  labwareIdsInTask: string[];
  instrument: InstrumentForLocation | undefined;
  userSpecifiedSlot: string | null;
  taskProperties: TaskProperties;
  labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId;
  labwareAndSourceTaskByTaskId: LabwareAndSourceTaskByTaskId;
  allTaskPropertiesById?: Record<string, AllTaskProperties>;
};

export const getSlotFillingRuleInput = (
  taskId: string,
  slot: string,
  instrumentId?: string | null
): MaybeWorkflowSerializationErrors<SlotFillingRuleInput | number> => {
  const parsedSlots = parseSlotString(slot);
  if (parsedSlots === null)
    return err([
      {
        errorType: 'tasks',
        message: TaskErrors.TASK_SLOT_FILLING_RULE_INVALID_SLOT_STRING,
        slot,
        taskId,
        instrumentId,
      },
    ]);

  if (parsedSlots.length === 1 && typeof parsedSlots[0] === 'number') {
    return ok(parsedSlots[0]);
  }

  return ok({
    range: parsedSlots.map(parsedSlotToSlotRangeInput),
    max_count: 1, // TODO(LT, 20/12/2024): Remove this when the max count default "1" is set in the API
  });
};

export const getActionTaskLabwareSources = ({
  instrument,
  labwareIdsInTask,
  taskProperties,
  userSpecifiedSlot,
  labwareAndSourceTaskByTaskId,
  allTaskPropertiesById,
}: GetActionTaskLabwareSourcesProps): MaybeWorkflowSerializationErrors<{
  [labwareId: string]: LabwareSourceInput;
}> => {
  const labwareSources: { [labwareId: string]: LabwareSourceInput } = {};
  const errors: WorkflowSerializationError[] = [];

  labwareIdsInTask.forEach((labwareId, index) => {
    const entry = labwareAndSourceTaskByTaskId[taskProperties.id]?.find(
      (labware) => labware.labwareId === labwareId
    );
    const sourceTaskId = entry?.sourceTaskId ?? null;
    const sourceTaskIds =
      entry?.sourceTaskIds && entry.sourceTaskIds.length > 1
        ? entry.sourceTaskIds
        : undefined;

    const initialIds: string[] = sourceTaskIds
      ? [...sourceTaskIds]
      : sourceTaskId !== null
      ? [sourceTaskId]
      : [];

    let hadLabwareParent = false;
    const resolvedIds = Array.from(
      new Set(
        initialIds.flatMap((id) => {
          const isContinuousLoading =
            allTaskPropertiesById?.[id]?.propertiesType ===
            PropertiesType.ContinuousLoading;

          if (!isContinuousLoading) return [id];

          const parentsEntry = labwareAndSourceTaskByTaskId[id]?.find(
            (lw) => lw.labwareId === labwareId
          );

          if (!parentsEntry) return [];

          if (
            parentsEntry.sourceTaskIds &&
            parentsEntry.sourceTaskIds.length > 0
          ) {
            return parentsEntry.sourceTaskIds;
          }
          if (parentsEntry.sourceTaskId) {
            return [parentsEntry.sourceTaskId];
          }
          hadLabwareParent = true;
          return [];
        })
      )
    );

    const adjustedSingleSourceTaskId =
      resolvedIds.length === 1
        ? resolvedIds[0]
        : resolvedIds.length === 0 &&
          (sourceTaskId === null || hadLabwareParent)
        ? null
        : null;
    const sourceTaskIdsResolved =
      resolvedIds.length > 1 ? resolvedIds : undefined;

    const getRawDestinationSlot = (): string => {
      if (
        instrument &&
        isTaskDoesNotSupportSlotAllocating(
          instrument?.driverName,
          taskProperties.taskType
        )
      ) {
        const slotParam = taskProperties.taskParameters?.slot;
        return typeof slotParam === 'string' || typeof slotParam === 'number'
          ? slotParam.toString()
          : '1';
      }
      if (instrument && supportsSlotRanges(instrument.driverName)) {
        const slot = taskProperties.labwareIdsToInputSlots?.[labwareId];
        return (slot ?? 1).toString();
      }
      // NOTE(IL, 2025-01-21): we probably don't want to use the labware index for its default slot,
      // it's arbitrary and it's not even stably ordered (it's from an Object.values in getActionTask)
      if (instrument && isSlotAllocatingInstrument(instrument.driverName)) {
        const slot = taskProperties.labwareIdsToInputSlots?.[labwareId];
        return (slot ?? 1).toString();
      }

      return (userSpecifiedSlot ?? index + 1).toString();
    };

    const rawDestinationSlot = getRawDestinationSlot();

    const parsedDestinationSlot = getSlotFillingRuleInput(
      taskProperties.id,
      rawDestinationSlot,
      instrument?.id
    );

    if (parsedDestinationSlot.isError) {
      errors.push(...parsedDestinationSlot.errors);
    } else {
      labwareSources[labwareId] = {
        type: 'labware_source',
        labware_id: labwareId,
        destination_slot: parsedDestinationSlot.data,
        source_task: sourceTaskIdsResolved
          ? { task_ids: sourceTaskIdsResolved }
          : adjustedSingleSourceTaskId,
        units_consumed: taskProperties.labwareIdsToUnitsConsumed[labwareId],
      };
    }
  });

  if (errors.length > 0) {
    return err(errors);
  }
  return ok(labwareSources);
};
